/* eslint-disable class-methods-use-this */
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  reauthenticateWithCredential,
  updateEmail,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  EmailAuthProvider,
  GoogleAuthProvider,
  FacebookAuthProvider
} from 'firebase/auth';
import {
  getFirestore,
  collection,
  doc,
  updateDoc,
  setDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  startAfter,
  limit,
  getDoc,
  getDocs,
  documentId
} from 'firebase/firestore';
import { getDatabase, ref, child, get, update } from 'firebase/database';
import {
  getStorage,
  ref as refStorage,
  uploadBytes,
  deleteObject,
  getDownloadURL
} from 'firebase/storage';
import { splitName, shuffleArray } from 'helpers/utils';
import firebaseConfig from './config';

class Firebase {
  constructor() {
    const firebaseApp = initializeApp(firebaseConfig);

    this.storage = getStorage(firebaseApp);
    this.rdb = getDatabase(firebaseApp);
    this.rdbRef = ref(this.rdb);
    this.db = getFirestore(firebaseApp);
    this.auth = getAuth(firebaseApp);

    // this.baseUrl = 'http://localhost:5000';
    this.baseUrl = 'https://server.uilpi.com';

    this.productsCollection = 'products';
    this.imagesCollection = 'images';
    this.clientLocation = 'Italia';
    this.clientCurrency = '€';
  }

  createAccount = (email, password) =>
    createUserWithEmailAndPassword(this.auth, email, password);

  signIn = (email, password) =>
    signInWithEmailAndPassword(this.auth, email, password);

  signInWithGoogle = () => signInWithPopup(this.auth, new GoogleAuthProvider());

  signInWithFacebook = () =>
    signInWithPopup(this.auth, new FacebookAuthProvider());

  signOut = () => signOut(this.auth);

  passwordReset = async (email) => {
    return new Promise((resolve, reject) => {
      const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email
        })
      };
      fetch(`${this.baseUrl}/api/external/passwordreset`, requestOptions)
        .then((response) => resolve(response.json()))
        .catch((error) => reject(error));
    });
  };

  getUser = async (id) => getDoc(doc(this.db, 'users', id));

  reauthenticate = (currentPassword) => {
    const user = this.auth.currentUser;
    const cred = EmailAuthProvider.credential(user.email, currentPassword);
    return reauthenticateWithCredential(user, cred);
  };

  updateEmail = (currentPassword, newEmail) =>
    new Promise((resolve, reject) => {
      this.reauthenticate(currentPassword)
        .then(() => {
          const user = this.auth.currentUser;
          updateEmail(user, newEmail)
            .then(() => {
              resolve('ok');
            })
            .catch((error) => reject(error));
        })
        .catch((error) => reject(error));
    });

  updateProfile = async (id, updates) => {
    await updateDoc(doc(this.db, 'users', id), updates);
    get(child(this.rdbRef, `users/${id}/chats`)).then((docSnapshot) => {
      docSnapshot.forEach((c) => {
        update(child(this.rdbRef, `chats/${c.key}/members/${id}`), {
          name: splitName(updates.fullname),
          avatar: updates.avatar
        });
      });
    });
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/x-www-form-urlencoded');
    const urlencoded = new URLSearchParams();
    urlencoded.append('user_id', id);
    if (updates.fullname) {
      urlencoded.append('nome', updates.fullname.split()[0]);
      urlencoded.append('cognome', updates.fullname.split()[1] || '');
      urlencoded.append('username', updates.fullname);
    }
    if (updates.avatar) urlencoded.append('avatar_name', updates.avatar);
    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: urlencoded,
      redirect: 'follow'
    };
    await fetch('https://live.uilpi.com/updateUser.php', requestOptions);
  };

  onAuthStateChanged = () =>
    new Promise((resolve, reject) => {
      onAuthStateChanged(this.auth, (user) => {
        if (user) {
          resolve(user);
        } else {
          reject(new Error('Auth state changed failed'));
        }
      });
    });

  addUser = async (id, user, password) => {
    await setDoc(doc(this.db, 'users', id), user);
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/x-www-form-urlencoded');
    const urlencoded = new URLSearchParams();
    urlencoded.append('username', user.fullname);
    urlencoded.append('nome', '');
    urlencoded.append('cognome', '');
    urlencoded.append('email', user.email);
    urlencoded.append('password', password);
    urlencoded.append('role', user.role);
    urlencoded.append('language', 'it');
    urlencoded.append('user_id', id);
    urlencoded.append('token', '');
    urlencoded.append('avatar', '');
    urlencoded.append('os', 'android');
    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: urlencoded,
      redirect: 'follow'
    };
    await fetch('https://live.uilpi.com/register.php', requestOptions);
  };

  deleteUser = (password) => {
    const user = this.auth.currentUser;
    return new Promise((resolve, reject) => {
      const credential = EmailAuthProvider.credential(user.email, password);
      reauthenticateWithCredential(user, credential)
        .then(() => {
          deleteDoc(doc(this.db, 'users', user.uid))
            .then(() => {
              getDocs(query(collection(this.db, this.productsCollection))).then(
                (snapshot) => {
                  snapshot.forEach((d) => {
                    if (d.data().userId === user.uid) {
                      deleteDoc(doc(this.db, this.productsCollection, d.id));
                      this.deleteProductImage(d.id);
                    }
                  });
                }
              );
              this.deleteUserImage(user.uid);

              const myHeaders = new Headers();
              myHeaders.append(
                'Content-Type',
                'application/x-www-form-urlencoded'
              );
              const urlencoded = new URLSearchParams();
              urlencoded.append('user_id', user.uid);
              const requestOptions = {
                method: 'POST',
                headers: myHeaders,
                body: urlencoded,
                redirect: 'follow'
              };
              fetch(
                'https://live.uilpi.com/cancellaAccount.php',
                requestOptions
              );
              user
                .delete()
                .then(() => {
                  resolve('ok');
                })
                .catch((err) => {
                  reject(err);
                });
            })
            .catch((err) => {
              reject(err);
            });
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  setAuthPersistence = () => setPersistence(this.auth, browserLocalPersistence);

  getSingleProduct = (id) => getDoc(doc(this.db, this.productsCollection, id));

  getClientLocation = async () => {
    const res = await fetch('https://ipapi.co/json')
      .then((response) => response.json())
      .catch(() => null);
    if (res !== null)
      if (res.country_code === 'CH') {
        this.clientLocation = 'Svizzera';
        this.clientCurrency = 'CHF';
      }
    return res;
  };

  getHomeObjects = async () => {
    const q1 = query(
      collection(this.db, 'ads'),
      where('location', '==', this.clientLocation)
    );
    const snapshot1 = await getDocs(q1);
    const ads = [];
    snapshot1.forEach((i) => ads.push({ id: i.id, ...i.data() }));
    const q2 = query(
      collection(this.db, this.imagesCollection),
      orderBy('position')
    );
    const snapshot2 = await getDocs(q2);
    const images = [];
    snapshot2.forEach((i) => images.push({ id: i.id, ...i.data() }));
    const shuffledAds = shuffleArray(ads);
    return { ads: shuffledAds, images };
  };

  getBrands = async () => {
    const q = query(collection(this.db, 'brands'), orderBy('name'));
    const snapshot = await getDocs(q);
    const brands = [];
    snapshot.forEach((i) => brands.push({ id: i.id, ...i.data() }));
    return { brands };
  };

  getPlans = async () => {
    const q = query(collection(this.db, 'plans'), orderBy('position'));
    const snapshot = await getDocs(q);
    const plans = [];
    snapshot.forEach((i) => plans.push({ id: i.id, ...i.data() }));
    return { plans };
  };

  getProducts = (lastRefKey) => {
    let didTimeout = false;

    return new Promise((resolve, reject) => {
      (async () => {
        if (lastRefKey) {
          try {
            const q = query(
              collection(this.db, this.productsCollection),
              where('visible', '==', true),
              where('sold', '==', false),
              where('address.country', '==', this.clientLocation),
              startAfter(lastRefKey),
              limit(12)
            );

            const snapshot = await getDocs(q);
            const products = [];
            snapshot.forEach((d) => products.push({ id: d.id, ...d.data() }));
            const lastKey = snapshot.docs[snapshot.docs.length - 1];

            resolve({ products, lastKey });
          } catch (e) {
            reject(e?.message);
          }
        } else {
          const timeout = setTimeout(() => {
            didTimeout = true;
            reject(new Error('Timeout error'));
          }, 15000);

          try {
            const totalQuery = await getDocs(
              query(
                collection(this.db, this.productsCollection),
                where('visible', '==', true),
                where('sold', '==', false),
                where('address.country', '==', this.clientLocation)
              )
            );
            const total = totalQuery.docs.length;
            const q = query(
              collection(this.db, this.productsCollection),
              where('visible', '==', true),
              where('sold', '==', false),
              where('address.country', '==', this.clientLocation),
              limit(12)
            );
            const snapshot = await getDocs(q);

            clearTimeout(timeout);
            if (!didTimeout) {
              const products = [];
              snapshot.forEach((d) => products.push({ id: d.id, ...d.data() }));
              const lastKey = snapshot.docs[snapshot.docs.length - 1];
              resolve({ products, lastKey, total });
            }
          } catch (e) {
            if (didTimeout) return;
            reject(e?.message);
          }
        }
      })();
    });
  };

  getOwnProducts = (payload) => {
    let didTimeout = false;

    return new Promise((resolve, reject) => {
      (async () => {
        if (payload.lastRefKey) {
          try {
            const q = query(
              collection(this.db, this.productsCollection),
              where('userId', '==', payload.user),
              startAfter(payload.lastRefKey),
              limit(12)
            );
            const snapshot = await getDocs(q);
            const products = [];
            snapshot.forEach((d) => products.push({ id: d.id, ...d.data() }));
            const lastKey = snapshot.docs[snapshot.docs.length - 1];

            resolve({ products, lastKey });
          } catch (e) {
            reject(e?.message);
          }
        } else {
          const timeout = setTimeout(() => {
            didTimeout = true;
            reject(new Error('Timeout error'));
          }, 15000);

          try {
            const q = query(
              collection(this.db, this.productsCollection),
              where('userId', '==', payload.user),
              limit(12)
            );
            const snapshot = await getDocs(q);

            clearTimeout(timeout);
            if (!didTimeout) {
              const products = [];
              snapshot.forEach((d) => products.push({ id: d.id, ...d.data() }));
              const lastKey = snapshot.docs[snapshot.docs.length - 1];
              const total = snapshot.docs.length;

              resolve({ products, lastKey, total });
            }
          } catch (e) {
            if (didTimeout) return;
            reject(e?.message);
          }
        }
      })();
    });
  };

  searchProducts = (searchKey) => {
    let didTimeout = false;

    return new Promise((resolve, reject) => {
      (async () => {
        const productsRef = collection(this.db, this.productsCollection);

        const timeout = setTimeout(() => {
          didTimeout = true;
          reject(new Error('Timeout error'));
        }, 15000);

        try {
          const searchedNameQ = query(
            productsRef,
            where('sold', '==', false),
            where('address.country', '==', this.clientLocation),
            where('name_lower', '>=', searchKey),
            where('name_lower', '<=', `${searchKey}\uf8ff`),
            orderBy('name_lower'),
            limit(12)
          );
          const searchedKeywordsQ = query(
            productsRef,
            where('sold', '==', false),
            where('address.country', '==', this.clientLocation),
            where('keywords', 'array-contains-any', searchKey.split(' ')),
            orderBy('dateAdded', 'desc'),
            limit(12)
          );

          const nameSnaps = await getDocs(searchedNameQ);
          const keywordsSnaps = await getDocs(searchedKeywordsQ);

          clearTimeout(timeout);
          if (!didTimeout) {
            const searchedNameProducts = [];
            const searchedKeywordsProducts = [];
            let lastKey = null;

            if (!nameSnaps.empty) {
              nameSnaps.forEach((d) => {
                searchedNameProducts.push({ id: d.id, ...d.data() });
              });
              lastKey = nameSnaps.docs[nameSnaps.docs.length - 1];
            }

            if (!keywordsSnaps.empty) {
              keywordsSnaps.forEach((d) => {
                searchedKeywordsProducts.push({ id: d.id, ...d.data() });
              });
            }

            const mergedProducts = [
              ...searchedNameProducts,
              ...searchedKeywordsProducts
            ];
            const hash = {};

            mergedProducts.forEach((product) => {
              hash[product.id] = product;
            });

            resolve({ products: Object.values(hash), lastKey });
          }
        } catch (e) {
          if (didTimeout) return;
          reject(e);
        }
      })();
    });
  };

  getLatestProducts = (itemsCount = 4) =>
    getDocs(
      query(
        collection(this.db, this.productsCollection),
        where('sold', '==', false),
        where('visible', '==', true),
        where('address.country', '==', this.clientLocation),
        orderBy('dateAdded', 'desc'),
        limit(itemsCount)
      )
    );

  getFeaturedProducts = (itemsCount = 12) =>
    getDocs(
      query(
        collection(this.db, this.productsCollection),
        where('featured', '==', true),
        where('visible', '==', true),
        where('sold', '==', false),
        where('address.country', '==', this.clientLocation),
        limit(itemsCount)
      )
    );

  getAdminFeaturedProducts = async (reverse) => {
    const docRef = doc(this.db, 'admin', 'products');
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const featuredArray = docSnap.data().featured;
      if (featuredArray.length === 0 && reverse)
        return getDocs(query(collection(this.db, this.productsCollection)));
      if (featuredArray.length > 0 && reverse)
        return getDocs(
          query(
            collection(this.db, this.productsCollection),
            where(documentId(), 'not-in', featuredArray)
          )
        );
      if (featuredArray.length === 0 && !reverse) return null;
      if (featuredArray.length > 0 && !reverse)
        return getDocs(
          query(
            collection(this.db, this.productsCollection),
            where(documentId(), 'in', featuredArray)
          )
        );
    }
    return null;
  };

  addProduct = (id, product) =>
    setDoc(doc(this.db, this.productsCollection, id), product);

  uploadImageCollection = async (imageCollection, imageKeys) => {
    const promises = imageCollection.map(async (img, i) => {
      return this.storeImage(imageKeys[i], 'products', img.file);
    });
    return Promise.all(promises);
  };

  addProductCompany = async (userId, payload) => {
    const key = this.generateKey();
    const { imageCollection } = payload;
    const downloadURL = await this.storeImage(key, 'products', payload.image);
    const image = { id: key, url: downloadURL };
    let images = [];
    if (imageCollection.length !== 0) {
      const imageKeys = imageCollection.map(() => this.generateKey());
      const imageUrls = await this.uploadImageCollection(
        imageCollection,
        imageKeys
      );
      images = imageUrls.map((url, i) => ({
        id: imageKeys[i],
        url
      }));
    }
    const product = {
      ...payload,
      image: downloadURL,
      imageCollection: [image, ...images]
    };
    const token = await this.getToken();
    return new Promise((resolve, reject) => {
      const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          product,
          key,
          userToken: token
        })
      };
      fetch(`${this.baseUrl}/api/product/company/add`, requestOptions)
        .then((response) => resolve(response.json()))
        .catch((error) => reject(error));
    });
  };

  generateKey = () => doc(collection(this.db, this.productsCollection)).id;

  storeImage = async (id, folder, imageFile) => {
    const snapshot = await uploadBytes(
      refStorage(this.storage, `${folder}/${id}`),
      imageFile
    );
    const downloadURL = await getDownloadURL(snapshot.ref);
    return downloadURL;
  };

  deleteProductImage = (id) =>
    deleteObject(refStorage(this.storage, `products/${id}`));

  deleteUserImage = async (id) => {
    await deleteObject(refStorage(this.storage, `avatar/${id}`));
    await deleteObject(refStorage(this.storage, `banner/${id}`));
  };

  editProduct = (id, updates) =>
    updateDoc(doc(this.db, this.productsCollection, id), updates);

  removeProduct = async (id) => {
    await deleteDoc(doc(this.db, this.productsCollection, id));
    this.deleteProductImage(id);
  };

  sendMessage = (chatId, recipientId, updates) =>
    new Promise((resolve, reject) => {
      const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chatId,
          recipientId,
          updates
        })
      };
      fetch(`${this.baseUrl}/api/chat/send`, requestOptions)
        .then((response) => resolve(response.json()))
        .catch((error) => reject(error));
    });

  setReadMessages = (id, userId) => {
    get(child(this.rdbRef, `chats/${id}/members/`)).then((docSnapshot) => {
      docSnapshot.forEach((c) => {
        if (c.key === userId) {
          update(child(this.rdbRef, `chats/${id}/members/${c.val().to}`), {
            unread: 0
          });
          update(child(this.rdbRef, `users/${userId}/chats/${id}`), {
            unread: 0
          });
        }
      });
    });
  };

  createChat = (payload) =>
    new Promise((resolve, reject) => {
      const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payload
        })
      };
      fetch(`${this.baseUrl}/api/chat/create`, requestOptions)
        .then((response) => resolve(response.json()))
        .catch((error) => reject(error));
    });

  toggleFeatured = async (productId) => {
    const token = await this.getToken();
    return new Promise((resolve, reject) => {
      const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productId,
          userToken: token
        })
      };
      fetch(`${this.baseUrl}/api/product/featured`, requestOptions)
        .then((response) => resolve(response.json()))
        .catch((error) => reject(error));
    });
  };

  changeImageSliderPosition = async (imageId, action, exPosition) => {
    const token = await this.getToken();
    return new Promise((resolve, reject) => {
      const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageId,
          action,
          exPosition,
          userToken: token
        })
      };
      fetch(`${this.baseUrl}/api/image/move`, requestOptions)
        .then((response) => resolve(response.json()))
        .catch((error) => reject(error));
    });
  };

  addHomeImage = async (image, length) => {
    const key = await doc(collection(this.db, this.imagesCollection)).id;
    const downloadUrl = await this.storeImage(key, 'home', image);
    const token = await this.getToken();
    return new Promise((resolve, reject) => {
      const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: key,
          src: downloadUrl,
          position: length + 1,
          userToken: token
        })
      };
      fetch(`${this.baseUrl}/api/image/add`, requestOptions)
        .then((response) => resolve(response.json()))
        .catch((error) => reject(error));
    });
  };

  removeProductByAdmin = async (productId) => {
    const token = await this.getToken();
    return new Promise((resolve, reject) => {
      const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productId,
          userToken: token
        })
      };
      fetch(`${this.baseUrl}/api/product/remove`, requestOptions)
        .then((response) => resolve(response.json()))
        .catch((error) => reject(error));
    });
  };

  removeUserByAdmin = async (userId) => {
    const token = await this.getToken();
    return new Promise((resolve, reject) => {
      const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          userToken: token
        })
      };
      fetch(`${this.baseUrl}/api/user/remove`, requestOptions)
        .then((response) => resolve(response.json()))
        .catch((error) => reject(error));
    });
  };

  removeImageByAdmin = async (imageId) => {
    const token = await this.getToken();
    return new Promise((resolve, reject) => {
      const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageId,
          userToken: token
        })
      };
      fetch(`${this.baseUrl}/api/image/remove`, requestOptions)
        .then((response) => resolve(response.json()))
        .catch((error) => reject(error));
    });
  };

  toggleUserBlocked = async (userId) => {
    const token = await this.getToken();
    return new Promise((resolve, reject) => {
      const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          userToken: token
        })
      };
      fetch(`${this.baseUrl}/api/user/blocked`, requestOptions)
        .then((response) => resolve(response.json()))
        .catch((error) => reject(error));
    });
  };

  addBrand = async (brand) => {
    const token = await this.getToken();
    return new Promise((resolve, reject) => {
      const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          brand,
          userToken: token
        })
      };
      fetch(`${this.baseUrl}/api/brand/add`, requestOptions)
        .then((response) => resolve(response.json()))
        .catch((error) => reject(error));
    });
  };

  removeBrand = async (brandId) => {
    const token = await this.getToken();
    return new Promise((resolve, reject) => {
      const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          brandId,
          userToken: token
        })
      };
      fetch(`${this.baseUrl}/api/brand/remove`, requestOptions)
        .then((response) => resolve(response.json()))
        .catch((error) => reject(error));
    });
  };

  addAd = async (image, location, orientation) => {
    const key = await doc(collection(this.db, 'ads')).id;
    const downloadUrl = await this.storeImage(key, 'home', image);
    const token = await this.getToken();
    return new Promise((resolve, reject) => {
      const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: key,
          src: downloadUrl,
          url: 'https://shop.uilpi.com',
          location,
          orientation,
          userToken: token
        })
      };
      fetch(`${this.baseUrl}/api/advertising/add`, requestOptions)
        .then((response) => resolve(response.json()))
        .catch((error) => reject(error));
    });
  };

  removeAd = async (adId) => {
    const token = await this.getToken();
    return new Promise((resolve, reject) => {
      const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          adId,
          userToken: token
        })
      };
      fetch(`${this.baseUrl}/api/advertising/remove`, requestOptions)
        .then((response) => resolve(response.json()))
        .catch((error) => reject(error));
    });
  };

  editUrlAd = async (adId, newUrl) => {
    const token = await this.getToken();
    return new Promise((resolve, reject) => {
      const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          adId,
          url: newUrl,
          userToken: token
        })
      };
      fetch(`${this.baseUrl}/api/advertising/edit`, requestOptions)
        .then((response) => resolve(response.json()))
        .catch((error) => reject(error));
    });
  };

  editPlan = async (planId, form) => {
    const token = await this.getToken();
    return new Promise((resolve, reject) => {
      const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          planId,
          plan: form,
          userToken: token
        })
      };
      fetch(`${this.baseUrl}/api/pricing/edit`, requestOptions)
        .then((response) => resolve(response.json()))
        .catch((error) => reject(error));
    });
  };

  getPayments = async () => {
    const token = await this.getToken();
    return new Promise((resolve, reject) => {
      const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userToken: token
        })
      };
      fetch(`${this.baseUrl}/api/payments/get`, requestOptions)
        .then((response) => resolve(response.json()))
        .catch((error) => reject(error));
    });
  };

  getToken = () =>
    this.auth.currentUser
      .getIdToken(true)
      .then((idToken) => idToken)
      .catch(() => null);

  getPDFLink = (fileName) => getDownloadURL(refStorage(this.storage, fileName));

  createPaypalOrder = async (currency, amount, userId) => {
    return fetch(`${this.baseUrl}/api/payments/paypal/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        currency,
        amount,
        userId
      })
    })
      .then((res) => {
        return res.json();
      })
      .then((orderData) => orderData.orderID);
  };

  capturePaypalOrder = (data, userId, selectedPlan) => {
    return fetch(`${this.baseUrl}/api/payments/paypal/capture`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        orderID: data.orderID,
        userId,
        selectedPlan
      })
    })
      .then((res) => {
        return res.json();
      })
      .then((orderData) => {
        return orderData;
      });
  };
}

const firebaseInstance = new Firebase();

export default firebaseInstance;
